/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Snackbar from '@mui/material/Snackbar';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDatePicker from 'components/MDDatePicker';
import MDDateRange from 'components/MDDateRange';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData, FetchURL } from 'services/Request';
import * as Func from 'services/Function';

// import ReceiptList from './ReceiptList';
import { Box, DialogActions, Stack, TextField, Typography } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import SendChangeStepper from './SendChangeStepper';

// import SearchCustomer from './SearchCustomer';
// import SearchInvoice from './SearchInvoice';

const stepperName = {
  edit: {
    name: 'แก้ไขข้อมูล',
    color: 'warning',
    icon: 'border_color',
  },
  waiting: {
    name: 'นำส่ง',
    color: 'warning',
    icon: 'local_shipping',
  },
  verify: {
    name: 'รอตรวจสอบ',
    color: 'warning',
    icon: 'verified',
  },
  success: {
    name: 'สำเร็จ',
    color: 'success',
    icon: 'task_alt',
  },
  cancel: {
    name: 'ยกเลิก',
    color: 'error',
    icon: 'cancel',
  },
};

const initAddAttr = {
  branch_id: '',
  cashier_id: '',
  shift_type_id: '',
  signature_cashier: '',
  signature_authority: '',
};

const initEditAttr = {
  signature: '',
};

const initAttr = {
  id: '',
  code: '',
  branch_id: '',
  cashier_id: '',
  shift_type_id: '',
  video_step1: '',
  video_step2: '',
  video_step3: '',
  signature_step1_cashier: '',
  signature_step1_authority: '',
  signature_step2: '',
  signature_step3: '',
  status: '',
  created_at: '',
  status_name: '',
  status_icon: '',
  status_color: '',
  branch_name: '',
  cashier_name: '',
  shift_type_name: '',
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  branch: 'ALL',
  status: 'ALL',
  period: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const cboStatus = [
  { id: 'ALL', name: 'ทั้งหมด' },
  { id: 'edit', name: stepperName.edit.name },
  { id: 'waiting', name: stepperName.waiting.name },
  { id: 'verify', name: stepperName.verify.name },
  { id: 'success', name: stepperName.success.name },
  { id: 'cancel', name: stepperName.cancel.name },
];

const initHeader = [
  { id: 'tb.status', label: 'สถานะ', align: 'center' },
  { id: 'tb.code', label: 'รหัส', align: 'center' },
  { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
  { id: 'tb.cashier_id', label: 'POS', align: 'left' },
  { id: 'tb.shift_type_id', label: 'กะ', align: 'left' },
  { id: 'tb.create_at', label: 'วันที่สร้าง', align: 'left' },
];

function SendChange() {
  const mod = 'send_change';
  const uploadUrl = 'https://form.ruangthongpharmacy.com/service/api/upload';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [list, setList] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [addAttr, setAddAttr] = useState(initAddAttr);
  const [editAttr, setEditAttr] = useState(initEditAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [stepper, setStepper] = useState({});
  const [cboBranch, setCboBranch] = useState([]);
  const [cboCashier, setCboCashier] = useState([]);
  const [cboShiftType, setCboShiftType] = useState([]);
  const [modalAdd, setModalAdd] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const signRef = useRef(null);
  const signCashierRef = useRef(null);
  const signAuthorityRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [tab, setTab] = useState(0);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const handleSelectFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      branch: data.branch,
      status: data.status,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const stepInt = () => {
    if (attr.status === 'edit') return 1;
    if (attr.status === 'waiting') return 2;
    if (attr.status === 'verify') return 3;
    if (attr.status === 'success') return 4;
    if (attr.status === 'cancel') return 5;

    return 0;
  };

  const getStepInt = (status) => {
    if (status === 'edit') return 1;
    if (status === 'waiting' || status === 'verify' || status === 'success') return 2;

    return 0;
  };

  const open = () => {
    setLoading(true);
    setAttr(initAttr);
    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setList(result.list);
        setNavigate(result.navigate);
        setStepper(result.stepper);
        setLoading(false);
        setTab(getStepInt(result.data.status));
        setFile(null);
        setPreview('');
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  function base64ToFile(base64, filename) {
    const [meta, data] = base64.split(',');
    const mime = meta.match(/:(.*?);/)?.[1] ?? 'application/octet-stream';

    const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
    return new File([bytes], filename, { type: mime });
  }

  const editWaiting = () => {
    if (!editAttr.signature || !file) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then(async (swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        const files = [
          { File: editAttr.signature, Name: 'signature_step2', isVideo: false },
          { File: file, Name: 'video_step2', isVideo: true },
        ];

        const formData = new FormData();
        formData.append('Path', 'SendChange');
        files.forEach((item, i) => {
          const realFile =
            typeof item.File === 'string' ? base64ToFile(item.File, `${item.Name}.png`) : item.File;

          formData.append(`Files[${i}].File`, realFile);
          formData.append(`Files[${i}].Name`, item.Name);
          formData.append(`Files[${i}].IsVideo`, String(item.isVideo));
        });

        const url = await FetchURL('POST', uploadUrl, app.token, formData, true);

        if (!url) {
          showNotify('เกิดข้อผิดพลาดในการอัพโหลดไฟล์');
          setLoading(false);
          return;
        }

        FetchData('OPTIONS', `/${mod}/waiting`, app.token, {
          id: id,
          signature_step2: url[0],
          video_step2: url[1],
        }).then((result) => {
          if (result.status === 'ok') {
            setFile(null);
            setPreview('');
            open();
            showNotify(t(`alert.addcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
          setLoading(false);
        });
      }
    });
  };

  const editVerify = () => {
    if (!editAttr.signature || !file) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then(async (swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        const files = [
          { File: editAttr.signature, Name: 'signature_step3', isVideo: false },
          { File: file, Name: 'video_step3', isVideo: true },
        ];

        const formData = new FormData();
        formData.append('Path', 'SendChange');
        files.forEach((item, i) => {
          const realFile =
            typeof item.File === 'string' ? base64ToFile(item.File, `${item.Name}.png`) : item.File;

          formData.append(`Files[${i}].File`, realFile);
          formData.append(`Files[${i}].Name`, item.Name);
          formData.append(`Files[${i}].IsVideo`, String(item.isVideo));
        });

        const url = await FetchURL('POST', uploadUrl, app.token, formData, true);

        if (!url) {
          showNotify('เกิดข้อผิดพลาดในการอัพโหลดไฟล์');
          setLoading(false);
          return;
        }

        FetchData('OPTIONS', `/${mod}/verify`, app.token, {
          id: id,
          signature_step3: url[0],
          video_step3: url[1],
        }).then((result) => {
          if (result.status === 'ok') {
            setFile(null);
            setPreview('');
            open();
            showNotify(t(`alert.addcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
          setLoading(false);
        });
      }
    });
  };

  const approve = (status) => {
    if (status !== 'cancel') {
      setTab(getStepInt(attr.status));
      return;
    }
    Swal.fire({
      title: `คุณต้องการเปลี่ยนสถานะเป็น \n"${stepperName[status].name}" ใช่หรือไม่ ?`,
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          status: status,
          value_type: attr.value_type,
        }).then((result) => {
          if (result.status === 'ok') {
            open();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setLoading(false);
            showNotify(result.msg, 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboBranch(result.branch);
        setCboCashier(result.cashier);
        setCboShiftType(result.shift_type);
        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const addConfirm = () => {
    if (
      !Func.verifyLength(addAttr.branch_id, 1) ||
      !Func.verifyLength(addAttr.signature_cashier, 1) ||
      !Func.verifyLength(addAttr.signature_authority, 1) ||
      !file
    )
      return;

    setConfirm(true);
  };

  const add = async () => {
    setLoading(true);
    setModalAdd(false);

    const files = [
      { File: addAttr.signature_cashier, Name: 'signature_step1_cashier', isVideo: false },
      { File: addAttr.signature_authority, Name: 'signature_step1_authority', isVideo: false },
      { File: file, Name: 'video_step1', isVideo: true },
    ];

    const formData = new FormData();
    formData.append('Path', 'SendChange');
    files.forEach((item, i) => {
      const realFile =
        typeof item.File === 'string' ? base64ToFile(item.File, `${item.Name}.png`) : item.File;

      formData.append(`Files[${i}].File`, realFile);
      formData.append(`Files[${i}].Name`, item.Name);
      formData.append(`Files[${i}].IsVideo`, String(item.isVideo));
    });

    const url = await FetchURL('POST', uploadUrl, app.token, formData, true);

    if (!url) {
      showNotify('เกิดข้อผิดพลาดในการอัพโหลดไฟล์');
      setLoading(false);
      return;
    }

    FetchData('PUT', `/${mod}`, app.token, {
      branch_id: addAttr.branch_id,
      cashier_id: addAttr.cashier_id ?? 0,
      shift_type_id: addAttr.shift_type_id ?? 0,
      signature_step1_cashier: url[0],
      signature_step1_authority: url[1],
      video_step1: url[2],
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`/${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDSelect
                  label='สาขา *'
                  labelDefault={t(`element.please_select`)}
                  data={cboBranch}
                  value={addAttr.branch_id}
                  onChange={(e) =>
                    setAddAttr({ ...addAttr, branch_id: e.target.value, cashier_id: '' })
                  }
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDSelect
                  label='POS'
                  labelDefault='โปรดเลือกสาขาก่อน'
                  data={cboCashier.filter(
                    (item) =>
                      item.branch_id === addAttr.branch_id || (item.id === '0' && addAttr.branch_id)
                  )}
                  value={addAttr.cashier_id}
                  onChange={(e) => setAddAttr({ ...addAttr, cashier_id: e.target.value })}
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDSelect
                  label='รอบการขาย'
                  labelDefault={t(`element.please_select`)}
                  data={cboShiftType}
                  value={addAttr.shift_type_id}
                  onChange={(e) => setAddAttr({ ...addAttr, shift_type_id: e.target.value })}
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  {preview && (
                    <Box
                      component='video'
                      src={preview}
                      alt='preview'
                      controls
                      sx={{
                        mt: 2,
                        width: '100%',
                        borderRadius: 2,
                        border: '1px solid #ddd',
                      }}
                    />
                  )}
                  <MDButton color='info' variant='outlined' component='label' fullWidth>
                    วิดีโอหลักฐาน
                    <input type='file' hidden accept='video/*' onChange={handleSelectFile} />
                  </MDButton>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography>ลายเซ็นแคชเชียร์</Typography>
                  <MDButton
                    size='small'
                    color='error'
                    variant='outlined'
                    onClick={() => {
                      signCashierRef.current?.clear();
                      setAddAttr((prev) => ({ ...prev, signature_cashier: '' }));
                    }}
                  >
                    ล้างลายเซ็น
                  </MDButton>
                </Stack>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    width: '100%',
                    height: 200,
                  }}
                >
                  <SignatureCanvas
                    ref={signCashierRef}
                    penColor='black'
                    canvasProps={{ style: { width: '100%', height: '100%' } }}
                    onEnd={() => {
                      const dataURL = signCashierRef.current?.getCanvas().toDataURL('image/png');

                      setAddAttr((prev) => ({
                        ...prev,
                        signature_cashier: dataURL || '',
                      }));
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography>ลายเซ็นผู้ตรวจสอบ</Typography>
                  <MDButton
                    size='small'
                    color='error'
                    variant='outlined'
                    onClick={() => {
                      signAuthorityRef.current?.clear();
                      setAddAttr((prev) => ({ ...prev, signature_authority: '' }));
                    }}
                  >
                    ล้างลายเซ็น
                  </MDButton>
                </Stack>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    width: '100%',
                    height: 200,
                  }}
                >
                  <SignatureCanvas
                    ref={signAuthorityRef}
                    penColor='black'
                    canvasProps={{ style: { width: '100%', height: '100%' } }}
                    onEnd={() => {
                      const dataURL = signAuthorityRef.current?.getCanvas().toDataURL('image/png');

                      setAddAttr((prev) => ({
                        ...prev,
                        signature_authority: dataURL || '',
                      }));
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>{t('element.show_all')}</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={7}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                signCashierRef.current.clear();
                signAuthorityRef.current.clear();
                setAddAttr(initAddAttr);
                setFile(null);
                setPreview('');
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center' width={160}>
                        {Func.statusDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.cashier_name}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.shift_type_name}
                      </TableCell>
                      <TableCell>
                        <MDBox className='cut'>{Func.dateDisplay(row.created_at)}</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'edit':
          return key === 'waiting' || key === 'cancel';
        case 'waiting':
          return key === 'verify';
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step>
          <StepLabel StepIconComponent={SendChangeStepper}>
            {action(key) ? (
              <MDButton
                variant='gradient'
                color={stepperName[key].color}
                size='small'
                onClick={() => approve(key)}
              >
                <Icon>{stepperName[key].icon}</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant='contained'
                  color={stepperName[key].color}
                  size='md'
                  container
                />
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('waiting')}
          {stepLevel('verify')}
          {stepLevel('success')}
          {stepLevel('cancel')}
        </Stepper>
      </MDBox>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2} my={2}>
        {/* <Grid item xs={12}> */}
        <Grid item sm={12} xs={12} md={4}>
          <TextField
            label='สาขา'
            labelDefault={t(`element.please_select`)}
            variant='standard'
            value={attr.branch_name}
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item sm={6} xs={12} md={4}>
          <TextField
            label='POS'
            labelDefault={t(`element.please_select`)}
            variant='standard'
            value={attr.cashier_name}
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item sm={6} xs={12} md={4}>
          <TextField
            label='รอบการขาย'
            labelDefault={t(`element.please_select`)}
            variant='standard'
            value={attr.shift_type_name}
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        {/* </Grid> */}
      </Grid>
    );
  };

  const tab0render = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6}>
          <MDBox className='card' p={2}>
            <Grid container>
              <Grid item xs={6}>
                <MDTypography variant='h5' mb={3}>
                  วิดีโอหลักฐาน
                </MDTypography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9
                }}
              >
                <iframe
                  src={attr.video_step1}
                  title='video'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                />
              </Box>
            </Grid>
          </MDBox>
        </Grid>
        <Grid item sm={12} md={6}>
          <MDBox className='card' p={2}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Grid container>
                  <Grid item xs={6}>
                    <MDTypography variant='h5' mb={3}>
                      ลายเซ็นแคชเชียร์
                    </MDTypography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    component='img'
                    src={attr.signature_step1_cashier}
                    alt='image'
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      border: '2px solid #ccc',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container>
                  <Grid item xs={6}>
                    <MDTypography variant='h5' mb={3}>
                      ลายเซ็นผู้ตรวจสอบ
                    </MDTypography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    component='img'
                    src={attr.signature_step1_authority}
                    alt='image'
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      border: '2px solid #ccc',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const tab1renderEdit = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6}>
          <Grid container>
            <Grid item xs={12}>
              <MDTypography variant='h5' mb={3}>
                แนบวิดีโอหลักฐาน
              </MDTypography>
            </Grid>
          </Grid>
          <Box>
            {preview && (
              <Box
                component='video'
                src={preview}
                alt='preview'
                controls
                sx={{
                  mt: 2,
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid #ddd',
                }}
              />
            )}
            <MDButton color='info' variant='outlined' component='label' fullWidth>
              วิดีโอหลักฐาน
              <input type='file' hidden accept='video/*' onChange={handleSelectFile} />
            </MDButton>
          </Box>
        </Grid>
        <Grid item sm={12} md={6}>
          <Grid container>
            <Grid item xs={12}>
              <MDTypography variant='h5' mb={3}>
                ลายเซ็นผู้ดำเนินการส่ง
              </MDTypography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton
                size='small'
                color='error'
                variant='outlined'
                onClick={() => {
                  signRef.current?.clear();
                  setEditAttr((prev) => ({ ...prev, signature: '' }));
                }}
              >
                ล้างลายเซ็น
              </MDButton>
            </MDBox>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: 2,
                width: '100%',
                height: 200,
              }}
            >
              <SignatureCanvas
                ref={signRef}
                penColor='black'
                canvasProps={{ style: { width: '100%', height: '100%' } }}
                onEnd={() => {
                  const dataURL = signRef.current?.getCanvas().toDataURL('image/png');

                  setEditAttr((prev) => ({
                    ...prev,
                    signature: dataURL || '',
                  }));
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} my={2}>
            <MDBox display='flex' justifyContent='center' gap={1}>
              <MDButton
                variant='outlined'
                color='success'
                size='small'
                onClick={editWaiting}
                fullWidth
                sx={{ py: 2 }}
              >
                <Icon>save</Icon>&nbsp;{t(`element.save`)}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const tab1render = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6}>
          <MDBox className='card' p={2}>
            <Grid container>
              <Grid item xs={6}>
                <MDTypography variant='h5' mb={3}>
                  วิดีโอหลักฐาน
                </MDTypography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9
                }}
              >
                <iframe
                  src={attr.video_step2}
                  title='video'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                />
              </Box>
            </Grid>
          </MDBox>
        </Grid>
        <Grid item sm={12} md={6}>
          <MDBox className='card' p={2}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={6}>
                    <MDTypography variant='h5' mb={3}>
                      ลายเซ็นผู้ดำเนินการ
                    </MDTypography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    component='img'
                    src={attr.signature_step2}
                    alt='image'
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      border: '2px solid #ccc',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const tab2renderEdit = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6}>
          <Grid container>
            <Grid item xs={12}>
              <MDTypography variant='h5' mb={3}>
                แนบวิดีโอหลักฐาน
              </MDTypography>
            </Grid>
          </Grid>
          <Box>
            {preview && (
              <Box
                component='video'
                src={preview}
                alt='preview'
                controls
                sx={{
                  mt: 2,
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid #ddd',
                }}
              />
            )}
            <MDButton color='info' variant='outlined' component='label' fullWidth>
              วิดีโอหลักฐาน
              <input type='file' hidden accept='video/*' onChange={handleSelectFile} />
            </MDButton>
          </Box>
        </Grid>
        <Grid item sm={12} md={6}>
          <Grid container>
            <Grid item xs={12}>
              <MDTypography variant='h5' mb={3}>
                ลายเซ็นผู้ตรวจสอบ
              </MDTypography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton
                size='small'
                color='error'
                variant='outlined'
                onClick={() => {
                  signRef.current?.clear();
                  setEditAttr((prev) => ({ ...prev, signature: '' }));
                }}
              >
                ล้างลายเซ็น
              </MDButton>
            </MDBox>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: 2,
                width: '100%',
                height: 200,
              }}
            >
              <SignatureCanvas
                ref={signRef}
                penColor='black'
                canvasProps={{ style: { width: '100%', height: '100%' } }}
                onEnd={() => {
                  const dataURL = signRef.current?.getCanvas().toDataURL('image/png');

                  setEditAttr((prev) => ({
                    ...prev,
                    signature: dataURL || '',
                  }));
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} my={2}>
            <MDBox display='flex' justifyContent='center' gap={1}>
              <MDButton
                variant='outlined'
                color='success'
                size='small'
                onClick={editVerify}
                fullWidth
                sx={{ py: 2 }}
              >
                <Icon>save</Icon>&nbsp;{t(`element.save`)}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const tab2render = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6}>
          <MDBox className='card' p={2}>
            <Grid container>
              <Grid item xs={6}>
                <MDTypography variant='h5' mb={3}>
                  วิดีโอหลักฐาน
                </MDTypography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9
                }}
              >
                <iframe
                  src={attr.video_step3}
                  title='video'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 0,
                  }}
                />
              </Box>
            </Grid>
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <MDBox className='card' p={2}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={6}>
                    <MDTypography variant='h5' mb={3}>
                      ลายเซ็นผู้ตรวจสอบ
                    </MDTypography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    component='img'
                    src={attr.signature_step3}
                    alt='image'
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 2,
                      border: '2px solid #ccc',
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            {renderStepper()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDTypography variant='h3'>#{attr.code} &nbsp;</MDTypography>
                {renderInfo()}
                <Tabs variant='fullWidth' value={tab} onChange={(e, newValue) => setTab(newValue)}>
                  <Tab icon={<Icon>border_color</Icon>} label='แก้ไข' />
                  {stepInt() === 5 ? null : (
                    <Tab icon={<Icon>local_shipping</Icon>} label='นำส่ง' />
                  )}
                  {stepInt() < 2 || stepInt() === 5 ? null : (
                    <Tab icon={<Icon>verified</Icon>} label='ตรวจสอบ' />
                  )}
                </Tabs>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2} sx={{ p: 2 }}>
                    {tab0render()}
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <Grid container spacing={2} sx={{ p: 2 }}>
                    {attr.status === 'edit' ? tab1renderEdit() : tab1render()}
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  <Grid container spacing={2} sx={{ p: 2 }}>
                    {attr.status === 'waiting' ? tab2renderEdit() : tab2render()}
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.pos`), t(`menu.send_change`)]} />
      {renderLoading()}
      {renderPage()}
      {renderAdd()}
      {/* {renderPrint()} */}
      <Footer />
      {notify}
    </DashboardLayout>
  );
}

export default SendChange;
